import React, { useEffect, useState } from "react";
import { API } from "@/lib/api";

/**
 * Renders the configured company logo (fetched from /api/settings/logo).
 * Falls back to a coloured rounded square with the supplied character when
 * no logo has been uploaded yet. The component subscribes to a global
 * "logo-updated" custom event so Settings can refresh it without reloading.
 *
 * Implementation note: instead of mounting an <img src=""/> first and waiting
 * for the load to fail, we ping the endpoint once and decide which element
 * to render — avoids the spurious "An empty string was passed to the src
 * attribute" warning in the browser console.
 */
export default function BrandLogo({ size = 40, fallbackChar = "M", tone = "light", className = "" }) {
  // null = unknown (don't render anything yet), string = ok, false = no logo
  const [src, setSrc] = useState(null);
  const [bust, setBust] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const url = `${API}/settings/logo?v=${bust}`;
    fetch(url, { method: "GET", credentials: "include" })
      .then((res) => {
        if (cancelled) return;
        setSrc(res.ok ? url : false);
      })
      .catch(() => { if (!cancelled) setSrc(false); });
    return () => { cancelled = true; };
  }, [bust]);

  useEffect(() => {
    const handler = () => setBust((n) => n + 1);
    window.addEventListener("brand-logo-updated", handler);
    return () => window.removeEventListener("brand-logo-updated", handler);
  }, []);

  // Show fallback while resolving OR when no logo is set.
  if (!src) {
    const fallbackBg = tone === "dark" ? "bg-[#2E86C1]" : "bg-[#2E86C1]";
    return (
      <div
        className={`${fallbackBg} text-white flex items-center justify-center font-heading font-bold rounded ${className}`}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
        data-testid="brand-logo-fallback"
        aria-hidden
      >
        {fallbackChar}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt="Company logo"
      onError={() => setSrc(false)}
      className={`object-contain bg-white rounded ${className}`}
      style={{ width: size, height: size }}
      data-testid="brand-logo-img"
    />
  );
}

export function notifyLogoUpdated() {
  try { window.dispatchEvent(new CustomEvent("brand-logo-updated")); } catch {}
}
